import React, { useState, useEffect } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdvertiserHeader from "./AdvertiserHeader";


// Import Icons
import {
  FaThLarge, FaAd, FaVideo, FaHistory,
  FaMoneyBillWave, FaUserCircle, FaCog, FaSignOutAlt, FaPlus, FaWallet
} from "react-icons/fa";

const AdvertiserLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [user, setUser] = useState({ name: "Advertiser", email: "" });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    try {
      if (userData && userData !== 'undefined') {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        setIsProfileComplete(!!parsed.name && !!parsed.email);
      }
    } catch (err) {
      console.error("Invalid user JSON in localStorage:", err);
    }
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Logout Confirmation",
      text: "Are you sure you want to end your session?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Logout",
      background: "#ffffff",
      customClass: {
        popup: "rounded-3xl shadow-2xl",
        confirmButton: "rounded-xl font-bold px-6",
        cancelButton: "rounded-xl font-bold px-6",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    });
  };

  const menuItems = [
    {
      title: "Navigation",
      items: [
        { path: "dashboard", icon: FaThLarge, label: "Dashboard", iconColor: "text-indigo-600", bg: "hover:bg-indigo-50" },
      ]
    },
    {
      title: "Ad Controls",
      items: [
        { path: "post/advertisement", icon: FaAd, label: "Launch Campaign", iconColor: "text-rose-600", bg: "hover:bg-rose-50" },
        { path: "leads-status", icon: FaVideo, label: "Live Tracking", iconColor: "text-blue-600", bg: "hover:bg-blue-50" },
      ]
    },
    {
      title: "Finances",
      items: [
        { path: "wallet", icon: FaMoneyBillWave, label: "My Capital", iconColor: "text-emerald-600", bg: "hover:bg-emerald-50" },
        { path: "advertiserpaymenthistory", icon: FaHistory, label: "Debit Logs", iconColor: "text-amber-600", bg: "hover:bg-amber-50" },
        { path: "addmoney", icon: FaMoneyBillWave, label: "Top Up", iconColor: "text-violet-600", bg: "hover:bg-violet-50" },
      ]
    },
    {
      title: "Management",
      items: [
        { path: "profile", icon: FaUserCircle, label: "Security & Profile", iconColor: "text-slate-600", bg: "hover:bg-slate-50" },
        { path: "changepassword", icon: FaCog, label: "Preferences", iconColor: "text-gray-600", bg: "hover:bg-gray-100" },
      ]
    }
  ];

  const bottomNavItems = [
    { path: "dashboard", icon: FaThLarge, label: "Home" },
    { path: "post/advertisement", icon: FaAd, label: "Post" },
    { path: "leads-status", icon: FaVideo, label: "Status" },
    { path: "wallet", icon: FaMoneyBillWave, label: "Wallet" },
    { path: "profile", icon: FaUserCircle, label: "Profile" }
  ];

  return (
    <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans antialiased text-slate-900">
      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] lg:hidden transition-all duration-500" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Desktop Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-[70] w-72 bg-white border-r border-slate-100 transform transition-all duration-500 ease-[cubic-bezier(0.4, 0, 0.2, 1)]
        lg:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
        flex flex-col shadow-2xl lg:shadow-none
      `}>
        {/* Brand Header */}
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <FaAd className="text-white text-base" />
            </div>
            <span className="text-lg font-[1000] tracking-tight text-slate-900">IINSAF<span className="text-indigo-600">.</span></span>
          </div>
          <button className="lg:hidden p-2 text-slate-400 hover:text-slate-900" onClick={() => setIsSidebarOpen(false)}>✕</button>
        </div>

        {/* User Card */}
        <div className="mx-4 p-3 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-indigo-600 font-black text-xs">
            {user.name?.[0] || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-slate-900 truncate">{user.name || "Advertiser"}</p>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">Premium Brand</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 space-y-4 custom-scrollbar pb-6 mt-2">
          {menuItems.map((section, idx) => (
            <div key={idx} className="space-y-1">
              <h3 className="px-3 text-[10px] font-[900] text-slate-300 uppercase tracking-[0.2em] mb-1">{section.title}</h3>
              <div className="space-y-0.5">
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname === `/advertiser/${item.path}`;
                  return (
                    <button
                      key={itemIdx}
                      onClick={() => { navigate(`/advertiser/${item.path}`); setIsSidebarOpen(false); }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 group
                        ${isActive
                          ? "bg-slate-900 text-white shadow-lg shadow-slate-200"
                          : `text-slate-500 ${item.bg} hover:text-slate-900 active:scale-95`
                        }
                      `}
                    >
                      <item.icon className={`text-base transition-transform duration-300 group-hover:scale-110 ${isActive ? 'text-white' : item.iconColor}`} />
                      <span className="text-[13px] font-black tracking-tight">{item.label}</span>
                      {isActive && <div className="ml-auto w-1 h-1 rounded-full bg-white animate-pulse" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="pt-4 border-t border-slate-100">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-rose-500 hover:bg-rose-50 font-black text-[13px] active:scale-95 transition-all">
              <FaSignOutAlt className="text-base" />
              <span>Logout</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        <AdvertiserHeader toggleSidebar={() => setIsSidebarOpen(true)} isProfileComplete={isProfileComplete} />

        <main className="flex-1 overflow-y-auto custom-scrollbar relative px-3 sm:px-6 py-4 pb-28 lg:pb-6 bg-[#F8FAFC]">
          <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Overlay for mobile bottom sub-menus - catches clicks outside to close */}
      {showCreateMenu && !isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-[55] transition-opacity duration-300"
          onClick={() => setShowCreateMenu(false)}
        />
      )}

      {/* Mobile Professional Bottom Navigation */}
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-[60] pointer-events-none transition-transform duration-300 ${isSidebarOpen ? 'translate-y-full' : 'translate-y-0'}`}>
        <div className="relative w-full bg-white border-t border-slate-100 pb-safe shadow-[0_-8px_30px_rgba(0,0,0,0.08)] pointer-events-auto z-50">

          {/* Pop-up Menu */}
          {showCreateMenu && (
            <div className="absolute bottom-[100%] inset-x-0 mx-auto mb-5 w-[calc(100vw-60px)] max-w-[280px] bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-white/40 p-2 z-[70] flex gap-2 animate-slide-up origin-bottom">
              <button
                onClick={() => { navigate('/advertiser/post/advertisement'); setShowCreateMenu(false); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-rose-500 to-rose-700 text-white transition-all active:scale-95 shadow-md shadow-rose-100/50"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaAd className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Create Ads</p>
                <p className="text-[6px] font-bold text-white/60 uppercase">Campaign</p>
              </button>

              <button
                onClick={() => { navigate('/advertiser/addmoney'); setShowCreateMenu(false); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-violet-500 to-violet-700 text-white transition-all active:scale-95 shadow-md shadow-violet-100/50"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaMoneyBillWave className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Top Up</p>
                <p className="text-[6px] font-bold text-white/60 uppercase">Wallet</p>
              </button>
            </div>
          )}

          {/* Bottom Bar Icons */}
          <div className="flex items-center justify-between h-[72px] px-2 sm:px-6">
            <div className="flex-1 flex justify-around">
              <button onClick={() => navigate('/advertiser/dashboard')} className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('dashboard') ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <FaThLarge className="w-5 h-5 mb-0.5" />
                <span className="text-[8px] font-black uppercase tracking-tighter">Home</span>
              </button>

              <button onClick={() => navigate('/advertiser/leads-status')} className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('leads-status') ? 'text-indigo-600 scale-110' : 'text-slate-400 hover:text-slate-600'}`}>
                <FaVideo className="w-5 h-5 mb-0.5" />
                <span className="text-[8px] font-black uppercase tracking-tighter whitespace-nowrap">Track Ads</span>
              </button>
            </div>

            {/* Center FAB */}
            <div className="flex flex-col items-center justify-end h-full pb-1.5 px-2 min-w-[80px]">
              <div className="relative -top-3">
                <button
                  onClick={() => setShowCreateMenu(!showCreateMenu)}
                  className={`w-14 h-14 bg-gradient-to-tr from-indigo-600 to-blue-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-200 border-4 border-white transition-all active:scale-95 ${showCreateMenu ? "scale-105 shadow-indigo-400 rotate-45" : ""}`}
                >
                  <FaPlus className="w-6 h-6" />
                </button>
              </div>
              <span className={`text-[8px] font-black uppercase tracking-tighter leading-none px-2 py-1 rounded-full border transition-all ${showCreateMenu ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-indigo-600 border-slate-100 shadow-sm'}`}>
                Create
              </span>
            </div>

            <div className="flex-1 flex justify-around">
              <button onClick={() => navigate('/advertiser/wallet')} className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('wallet') ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <FaWallet className="w-5 h-5 mb-0.5" />
                <span className="text-[8px] font-black uppercase tracking-tighter">Wallet</span>
              </button>

              <button onClick={() => navigate('/advertiser/profile')} className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('profile') ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <FaUserCircle className="w-5 h-5 mb-0.5" />
                <span className="text-[8px] font-black uppercase tracking-tighter">Profile</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Support Assist - Adjusted position */}
      <button
        onClick={() => navigate("/contact")}
        className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 bg-white border border-slate-100 text-slate-900 w-12 h-12 rounded-xl shadow-xl flex items-center justify-center group hover:bg-slate-900 hover:text-white transition-all duration-300 z-40 active:scale-90"
      >
        <FaCog className="text-lg rotate-0 group-hover:rotate-180 transition-transform duration-1000" />
      </button>
    </div>
  );
};

export default AdvertiserLayout;
